<template>
  <div class="page-content edit-user-page">
    <!-- Estado cargando -->
    <div v-if="loading" class="edit-user-loading">
      <div class="edit-user-card edit-user-card--glass">
        <p class="edit-user-loading__text">Cargando usuario…</p>
      </div>
    </div>

    <!-- Contenido principal -->
    <div v-else-if="canEdit" class="edit-user-wrapper">
      <div class="edit-user-card edit-user-card--glass">
        <!-- Header -->
        <div class="edit-user-header">
          <div>
            <p class="edit-user-title">Editar usuario</p>
            <p class="edit-user-subtitle">
              Actualiza tu información de acceso.
            </p>
          </div>

          <div class="edit-user-badge-group">
            <span class="edit-user-badge">
              {{ currentRoleLabel }}
            </span>
          </div>
        </div>

        <!-- Formulario -->
        <form @submit.prevent="handleUpdate" class="edit-user-form">
          <!-- Nombre -->
          <div class="edit-user-field">
            <label class="edit-user-label">Nombre</label>
            <input
              v-model.trim="nombre"
              type="text"
              autocomplete="name"
              placeholder="Nombre y apellido"
              class="ios-input"
            />
          </div>

          <!-- Email -->
          <div class="edit-user-field">
            <label class="edit-user-label">Correo electrónico</label>
            <input
              v-model.trim="email"
              type="email"
              autocomplete="email"
              placeholder="ejemplo@mail.com"
              class="ios-input"
            />
          </div>

          <!-- Rol (solo para superadmin / admin) -->
          <div
            v-if="canManageRoles"
            class="edit-user-field"
          >
            <label class="edit-user-label">Rol</label>
            <select
              v-model="role"
              class="ios-input edit-user-select"
            >
              <option value="">-- Selecciona un rol --</option>
              <option
                v-for="option in availableRoles"
                :key="option.value"
                :value="option.value"
              >
                {{ option.text }}
              </option>
            </select>
          </div>

          <!-- Contraseña -->
          <div class="edit-user-field">
            <label class="edit-user-label">
              Contraseña
              <span class="edit-user-label-hint">
                (dejar vacío para mantener la actual)
              </span>
            </label>
            <input
              v-model="password"
              type="password"
              autocomplete="new-password"
              placeholder="Mínimo 10 caracteres"
              class="ios-input"
            />
          </div>

          <!-- Confirmar contraseña -->
          <div class="edit-user-field">
            <label class="edit-user-label">Confirmar contraseña</label>
            <input
              v-model="confirmPassword"
              type="password"
              autocomplete="new-password"
              placeholder="Repite tu contraseña"
              class="ios-input"
            />
          </div>

          <!-- Botón -->
          <button
            type="submit"
            class="ios-primary-button edit-user-submit"
            :disabled="saving"
          >
            <span v-if="!saving">Actualizar información</span>
            <span v-else class="loader-dot"></span>
          </button>
        </form>
      </div>
    </div>

    <!-- Sin permisos -->
    <VistaProhibida v-else />
  </div>
</template>

<script>
import axios from 'axios';
import VistaProhibida from '@/views/VistaProhibida.vue';
import { useToast } from 'vue-toastification';

export default {
  name: 'EditUser',
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  components: {
    VistaProhibida,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      nombre: '',
      email: '',
      role: '',
      password: '',
      confirmPassword: '',
      availableRoles: [],
      loading: true,
      saving: false,
      canEdit: false,
    };
  },
  computed: {
    userToken() {
      return this.$store.state.user?.token || null;
    },
    userRole() {
      return this.$store.state.role || null;
    },
    currentRoleLabel() {
      const map = {
        superadministrador: 'Super Administrador',
        administrador: 'Administrador',
        usuario: 'Usuario',
        repartidor: 'Repartidor',
        recolector: 'Recolector',
      };
      return map[this.role] || 'Usuario';
    },
    // ¿El usuario logueado puede gestionar roles?
    canManageRoles() {
      return (
        this.userRole === 'superadministrador' ||
        this.userRole === 'administrador'
      );
    },
  },
  async created() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;

      try {
        const response = await axios.get(`/api/user/${this.token}`);
        const userData = response.data?.user || response.data;

        // backend actual: { id_token, email, activo, imagen, type, role }
        this.nombre =
          userData.nombre ||
          userData.name ||
          ''; // si más adelante mandas nombre
        this.email = userData.email || '';
        this.role = userData.role || 'usuario';

        // roles que PUEDE asignar (según rol actual del logueado)
        this.availableRoles = this.getAvailableRoles();

        // ¿puede ver/editar este usuario?
        this.canEdit = this.checkPermissions();
      } catch (error) {
        console.error('Error al cargar los datos del usuario:', error);
        this.canEdit = false;
        if (this.toast) {
          this.toast.error('No se pudieron cargar los datos del usuario.');
        }
      } finally {
        this.loading = false;
      }
    },

    checkPermissions() {
      const currentUserRole = this.userRole;
      const isSelf = this.userToken === this.token;

      if (!currentUserRole) return false;

      // Solo superadmin, admin o el propio usuario pueden entrar
      if (currentUserRole === 'superadministrador') return true;
      if (currentUserRole === 'administrador') return true;

      // usuario / repartidor / recolector: solo su propio perfil
      if (
        currentUserRole === 'usuario' ||
        currentUserRole === 'repartidor' ||
        currentUserRole === 'recolector'
      ) {
        return isSelf;
      }

      return false;
    },

    getAvailableRoles() {
      const currentUserRole = this.userRole;

      const roleMap = {
        superadministrador: [
          { value: 'superadministrador', text: 'Super Administrador' },
          { value: 'administrador', text: 'Administrador' },
          { value: 'usuario', text: 'Usuario' },
          { value: 'repartidor', text: 'Repartidor' },
          { value: 'recolector', text: 'Recolector' },
        ],
        administrador: [
          { value: 'administrador', text: 'Administrador' },
          { value: 'usuario', text: 'Usuario' },
          { value: 'repartidor', text: 'Repartidor' },
          { value: 'recolector', text: 'Recolector' },
        ],
        usuario: [],
        repartidor: [],
        recolector: [],
      };

      return roleMap[currentUserRole] || [];
    },

      validatePassword(password) {
      if (!password || password.length < 10) return false;
      if (!/[A-Z]/.test(password)) return false; // mayúscula
      if (!/[a-z]/.test(password)) return false; // minúscula
      if (!/[0-9]/.test(password)) return false; // número
      return true;
    },

    async handleUpdate() {
      const errores = [];

      if (!this.validateEmail(this.email)) {
        errores.push('• Por favor, introduce un correo electrónico válido.');
      }

      // Solo validamos rol si el usuario logueado puede gestionarlo
      if (this.canManageRoles && !this.role) {
        errores.push('• Selecciona un rol para el usuario.');
      }

      if (this.password || this.confirmPassword) {
        if (this.password !== this.confirmPassword) {
          errores.push('• Las contraseñas no coinciden.');
        }
         if (!this.validatePassword(this.password)) {
          errores.push(
            '• La contraseña debe tener al menos 10 caracteres e incluir una mayúscula, una minúscula y un número.'
          );
        }
      }

      if (errores.length) {
        if (this.toast) {
          this.toast.error(errores.join('\n'));
        }
        return;
      }

      this.saving = true;

      try {
        const payload = {
          nombre: this.nombre,
          email: this.email,
          token: this.token,
        };

        // Solo superadmin/admin pueden cambiar rol
        if (this.canManageRoles) {
          payload.role = this.role;
        }

        if (this.password) {
          payload.password = this.password;
        }

        await axios.post(`/api/user/update/${this.token}`, payload);

        if (this.toast) {
          this.toast.success('Información actualizada correctamente.');
        }
      } catch (error) {
        console.error('Error al actualizar usuario:', error);
        if (this.toast) {
          this.toast.error('Hubo un problema al actualizar la información.');
        }
      } finally {
        this.saving = false;
      }
    },

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
  },
};
</script>

<style scoped>
.edit-user-page {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1.5rem 1rem;
}

.edit-user-wrapper {
  width: 100%;
  max-width: 560px;
}

.edit-user-card {
  border-radius: 1.6rem;
  padding: 1.75rem 1.5rem 1.5rem;
}

.edit-user-card--glass {
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.38),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(24px);
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.8);
}

/* Header */
.edit-user-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.4rem;
}

.edit-user-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #e5e7eb;
}

.edit-user-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
  margin-top: 0.1rem;
}

.edit-user-badge-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  align-items: flex-end;
}

.edit-user-badge {
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 0.18rem 0.6rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.75);
  color: #e5e7eb;
  background: rgba(15, 23, 42, 0.92);
}

/* Formulario */
.edit-user-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.edit-user-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.edit-user-label {
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #cbd5f5;
}

.edit-user-label-hint {
  font-weight: 400;
  font-size: 0.7rem;
  letter-spacing: normal;
  text-transform: none;
  color: #9ca3af;
  margin-left: 0.25rem;
}

/* Inputs glass (reutilizamos estilo de login/registro) */
.ios-input {
  width: 100%;
  border-radius: 1rem;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 0.7rem 0.95rem;
  font-size: 0.86rem;
  color: #e5e7eb;
  outline: none;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease,
    transform 0.12s ease;
  appearance: none;
}

.ios-input::placeholder {
  color: rgba(148, 163, 184, 0.9);
}

.ios-input:focus {
  border-color: rgba(52, 211, 153, 0.95);
  box-shadow:
    0 0 0 1px rgba(52, 211, 153, 0.7),
    0 18px 45px rgba(15, 23, 42, 0.85);
  background: rgba(15, 23, 42, 0.98);
  transform: translateY(-1px);
}

/* Select */
.edit-user-select {
  padding-right: 2.2rem;
  background-image: linear-gradient(
      45deg,
      transparent 50%,
      rgba(148, 163, 184, 0.9) 50%
    ),
    linear-gradient(
      135deg,
      rgba(148, 163, 184, 0.9) 50%,
      transparent 50%
    );
  background-position:
    calc(100% - 14px) 50%,
    calc(100% - 9px) 50%;
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
}

/* Botón */
.ios-primary-button {
  border-radius: 999px;
  padding: 0.7rem 1rem;
  font-size: 0.86rem;
  font-weight: 600;
  background: linear-gradient(
    135deg,
    #f9fafb,
    #e5e7eb
  );
  color: #020617;
  border: none;
  box-shadow:
    0 12px 30px rgba(15, 23, 42, 0.7),
    0 0 0 1px rgba(148, 163, 184, 0.3);
  cursor: pointer;
  transition:
    transform 0.12s ease,
    box-shadow 0.18s ease,
    background 0.18s ease,
    opacity 0.18s ease;
}

.ios-primary-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:
    0 16px 36px rgba(15, 23, 42, 0.85),
    0 0 0 1px rgba(148, 163, 184, 0.45);
}

.ios-primary-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow:
    0 10px 25px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(148, 163, 184, 0.35);
}

.ios-primary-button:disabled {
  opacity: 0.7;
  cursor: default;
}

.edit-user-submit {
  margin-top: 0.3rem;
}

/* Loader en botón */
.loader-dot {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 999px;
  border: 2px solid rgba(15, 23, 42, 0.85);
  border-top-color: rgba(15, 23, 42, 0.1);
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Cargando */
.edit-user-loading {
  width: 100%;
  max-width: 460px;
}

.edit-user-loading__text {
  font-size: 0.9rem;
  color: #e5e7eb;
}

/* Responsive */
@media (max-width: 640px) {
  .edit-user-card {
    padding-inline: 1rem;
  }
}
</style>
